// API Configuration
import axios from 'axios';

const getApiUrl = (): string => {
  // Check for environment variable (set during build)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Use relative URLs so requests go through the same origin.
  // In development, CRA proxy (package.json) forwards /api/* to localhost:8081.
  // In production, CloudFront/nginx proxies /api/* to the backend.
  // This ensures CSRF cookies (XSRF-TOKEN) are same-origin and always accessible.
  return '';
};

export const API_BASE_URL = getApiUrl();

// Set axios default base URL globally
axios.defaults.baseURL = API_BASE_URL;

// SOX Compliance: Configure axios to handle CSRF tokens
// Pick up the XSRF-TOKEN cookie and send it back in the X-XSRF-TOKEN header
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// Helper to read a cookie value by name
const getCookieValue = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/auth/me') || url.includes('/api/auth/login') || url.includes('/api/auth/logout');
};

const isJwtExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    if (!payload?.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  try { sessionStorage.removeItem('tenantConfig'); } catch {}

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Add request interceptor to include auth header and CSRF token
axios.interceptors.request.use(
  (config) => {
    // Add Authorization token if available
    // Tenant context is included in the JWT token - no need for separate header
    const token = localStorage.getItem('token');
    if (token) {
      if (isJwtExpired(token)) {
        clearAuthAndRedirect();
        return Promise.reject(new Error('Session expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // SOX Compliance: Explicitly attach CSRF token for state-changing requests
    const method = (config.method || '').toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCookieValue('XSRF-TOKEN');
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for handling common errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      const requestUrl = error.config?.url || '';

      if (!token || isJwtExpired(token) || isAuthEndpoint(requestUrl)) {
        clearAuthAndRedirect();
      } else {
        console.error('401 Unauthorized for API request without clearing active session:', requestUrl);
      }
    }
    
    // Handle 403 Forbidden - insufficient permissions
    if (error.response?.status === 403) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = user.role || 'Unknown';
      console.error('403 Forbidden - User role:', userRole);
      console.error('Required roles: ADMIN, TRADER, or PORTFOLIO_MANAGER');
      // Don't redirect, let the component handle the error display
    }
    
    return Promise.reject(error);
  }
);
